import { Result, TaggedError } from 'better-result';
import * as v from 'valibot';

/** Creates `nullish` schema and transforms any `null` values into `undefined` */
export const nullishAsOptional = <
  const TWrapped extends v.BaseSchema<unknown, unknown, v.BaseIssue<unknown>>,
>(
  wrapped: TWrapped,
) => v.nullish(wrapped, () => undefined);

export const paginated = <
  const TWrapped extends v.BaseSchema<unknown, unknown, v.BaseIssue<unknown>>,
>(
  wrapped: TWrapped,
) =>
  v.object({
    count: v.number(),
    next: nullishAsOptional(v.string()),
    previous: nullishAsOptional(v.string()),
    results: v.array(wrapped),
  });

const StringAsNumber = v.pipe(
  v.string(),
  v.transform((n) => Number(n)),
  v.number(),
);

/** Creates a `numeric` schema that can accept a string or a number and returns a number. */
export const numeric = () => v.union([StringAsNumber, v.number()]);

export class ValidationError extends TaggedError('ValidationError')<{
  issues: [v.BaseIssue<unknown>, ...v.BaseIssue<unknown>[]];
}> {
  constructor(args: { issues: [v.BaseIssue<unknown>, ...v.BaseIssue<unknown>[]] }) {
    super(args);
    this.message = v.summarize(args.issues);
    this.stack = this.stack?.replace('ValidationError', `ValidationError: ${this.message}`);
  }
}

export const betterParse = <const TSchema extends v.GenericSchema>(
  schema: TSchema,
  data: unknown,
): Result<v.InferOutput<TSchema>, ValidationError> => {
  const parsed = v.safeParse(schema, data);
  if (!parsed.success) {
    return Result.err(new ValidationError({ issues: parsed.issues }));
  }
  return Result.ok(parsed.output);
};
